import { useState, useEffect, useCallback } from "react";
import {
  Package, AlertTriangle, Plus, Edit2, Trash2, X, Check,
  Search, RefreshCw, TrendingDown, TrendingUp,
} from "lucide-react";
import { useAdmin } from "../admin-context";

const STATUS_CHIP = {
  ok:       { cls: "bg-green-100 text-green-800",  label: "In Stock"   },
  low:      { cls: "bg-yellow-100 text-yellow-800", label: "Low Stock"  },
  out:      { cls: "bg-red-100 text-red-800",       label: "Out of Stock" },
};

const stockStatus = (qty, threshold) => {
  if (qty <= 0)         return "out";
  if (qty <= threshold) return "low";
  return "ok";
};

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const InventoryPage = () => {
  const { inventory, loadInventory, upsertInventory, deleteInventory, products, showToast } = useAdmin();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | low | out
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ menu_item_id: "", menu_item_name: "", stock_quantity: 0, low_stock_threshold: 10, unit: "pcs" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadInventory(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ menu_item_id: "", menu_item_name: "", stock_quantity: 0, low_stock_threshold: 10, unit: "pcs" });
    setDrawerOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      menu_item_id: item.menu_item_id,
      menu_item_name: item.menu_item_name,
      stock_quantity: item.stock_quantity,
      low_stock_threshold: item.low_stock_threshold,
      unit: item.unit,
    });
    setDrawerOpen(true);
  };

  const handleProductSelect = (e) => {
    const prod = products.find((p) => String(p.id) === e.target.value);
    if (prod) setForm((f) => ({ ...f, menu_item_id: prod.id, menu_item_name: prod.name }));
  };

  const handleSave = async () => {
    if (!form.menu_item_id) return showToast("Select a product", "error");
    setSaving(true);
    await upsertInventory(form.menu_item_id, form);
    setSaving(false);
    setDrawerOpen(false);
    showToast("Inventory updated");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this item from inventory?")) return;
    await deleteInventory(id);
    showToast("Removed");
  };

  const list = (inventory || []).filter((item) => {
    const s = stockStatus(item.stock_quantity, item.low_stock_threshold);
    if (filter === "low" && s !== "low") return false;
    if (filter === "out" && s !== "out") return false;
    if (search && !item.menu_item_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const lowCount = (inventory || []).filter((i) => stockStatus(i.stock_quantity, i.low_stock_threshold) === "low").length;
  const outCount = (inventory || []).filter((i) => stockStatus(i.stock_quantity, i.low_stock_threshold) === "out").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Inventory</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track stock levels per menu item</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadInventory()} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600">
            <Plus size={16} /> Add Item
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Package size={20} className="text-blue-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Total Items</p>
            <p className="text-2xl font-bold text-gray-900">{(inventory || []).length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3 cursor-pointer hover:border-yellow-300" onClick={() => setFilter(f => f === "low" ? "all" : "low")}>
          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center"><TrendingDown size={20} className="text-yellow-600" /></div>
          <div>
            <p className="text-xs text-gray-500">Low Stock</p>
            <p className="text-2xl font-bold text-yellow-700">{lowCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3 cursor-pointer hover:border-red-300" onClick={() => setFilter(f => f === "out" ? "all" : "out")}>
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><AlertTriangle size={20} className="text-red-500" /></div>
          <div>
            <p className="text-xs text-gray-500">Out of Stock</p>
            <p className="text-2xl font-bold text-red-600">{outCount}</p>
          </div>
        </div>
      </div>

      {/* Filters + search */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items..."
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
          />
        </div>
        {["all", "low", "out"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-2 rounded-lg text-sm font-medium capitalize border transition ${filter === f ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {f === "all" ? "All" : f === "low" ? "Low Stock" : "Out of Stock"}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {["Product", "Stock", "Threshold", "Unit", "Status", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {list.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No items found</td></tr>
              ) : list.map((item) => {
                const s = stockStatus(item.stock_quantity, item.low_stock_threshold);
                const chip = STATUS_CHIP[s];
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{item.menu_item_name || `Item #${item.menu_item_id}`}</td>
                    <td className="px-4 py-3">
                      <span className={`font-bold text-lg ${s === "out" ? "text-red-600" : s === "low" ? "text-yellow-700" : "text-green-700"}`}>
                        {item.stock_quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.low_stock_threshold}</td>
                    <td className="px-4 py-3 text-gray-500">{item.unit}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(item)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Stock" : "Add Inventory Item"}>
        <div className="space-y-4">
          {!editing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                value={form.menu_item_id}
                onChange={handleProductSelect}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              >
                <option value="">— Select product —</option>
                {(products || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          {editing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <p className="text-sm text-gray-900 font-semibold px-3 py-2 bg-gray-50 rounded-lg">{form.menu_item_name}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity</label>
            <input
              type="number" min="0" value={form.stock_quantity}
              onChange={(e) => setForm((f) => ({ ...f, stock_quantity: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Low Stock Threshold</label>
            <input
              type="number" min="0" value={form.low_stock_threshold}
              onChange={(e) => setForm((f) => ({ ...f, low_stock_threshold: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <select
              value={form.unit}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
            >
              {["pcs", "kg", "g", "liters", "ml", "box", "pack"].map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleSave} disabled={saving}
            className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </Drawer>
    </div>
  );
};

export default InventoryPage;
